import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, ChevronDown, ChevronUp, Hotel, Plus, MapPin, Calendar, Users, Upload, Image as ImageIcon } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';

export const MEAL_PLANS = [
  { id: 'CP', label: 'CP (Continental)' },
  { id: 'MAP', label: 'MAP (Modified American)' },
  { id: 'AP', label: 'AP (American Plan)' },
  { id: 'EPAI', label: 'EPAI' },
  { id: 'CPAI', label: 'CPAI' },
  { id: 'MAPAI', label: 'MAPAI' },
  { id: 'APAI', label: 'APAI' },
  { id: 'ALL_INCLUSIVE', label: 'All Inclusive' }
];

const STAR_RATINGS = ['3 Star', '4 Star', '5 Star', '5 Star Deluxe', 'Boutique', 'Resort'];

// Correction 3 — accommodation type. "cruise" swaps the room fields for
// ship/cabin/port/sailing fields in the editor + preview.
const ACCOMMODATION_TYPES = [
  { id: 'hotel', label: 'Hotel' },
  { id: 'cruise', label: 'Cruise' },
  { id: 'resort', label: 'Resort' },
  { id: 'homestay', label: 'Homestay' },
  { id: 'camp', label: 'Camp' },
];

export default function HotelEditor({ hotels, onUpdate, onImageUpload, destinationPhotoOverrides, onDestinationPhotoUpload, onDestinationPhotoClear }) {
  const [expandedIndex, setExpandedIndex] = useState(0);

  // Backfill stable _id on legacy hotels loaded from DB so typing into the
  // hotelName field doesn't remount the card (would steal focus after 1 char).
  React.useEffect(() => {
    if (!Array.isArray(hotels) || hotels.length === 0) return;
    if (!hotels.some((h) => !h?._id)) return;
    onUpdate(hotels.map((h, i) => h?._id ? h : { ...(h || {}), _id: `h_legacy_${i}_${Date.now().toString(36)}` }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotels?.length]);

  const addHotel = () => {
    const newHotel = {
      _id: `h_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      hotelName: '',
      accommodation_type: 'hotel',
      optionNumber: null,
      starRating: '',
      address: '',
      city: '',
      checkInDate: '',
      checkOutDate: '',
      roomType: '',
      rooms: 1,
      pax_composition: '',
      extraBedIncluded: false,
      mealPlan: [],
      imageUrl: ''
    };
    onUpdate([...hotels, newHotel]);
    setExpandedIndex(hotels.length);
  };

  const updateHotel = (index, field, value) => {
    const updated = hotels.map((h, i) => i === index ? { ...h, [field]: value } : h);
    onUpdate(updated);
  };

  const removeHotel = (index) => {
    onUpdate(hotels.filter((_, i) => i !== index));
    if (expandedIndex >= hotels.length - 1) {
      setExpandedIndex(Math.max(0, hotels.length - 2));
    }
  };

  const toggleMealPlan = (index, mealId) => {
    const hotel = hotels[index];
    const currentMeals = hotel.mealPlan || [];
    const newMeals = currentMeals.includes(mealId)
      ? currentMeals.filter(m => m !== mealId)
      : [...currentMeals, mealId];
    updateHotel(index, 'mealPlan', newMeals);
  };

  const handleImageChange = async (index, e) => {
    const file = e.target.files?.[0];
    if (file && onImageUpload) {
      await onImageUpload(index, file);
    }
  };

  const cityKeyFor = (city) => (city || '').toLowerCase().trim();

  const handleDestPhotoChange = async (city, e) => {
    const file = e.target.files?.[0];
    if (file && onDestinationPhotoUpload) {
      await onDestinationPhotoUpload(cityKeyFor(city), file);
    }
  };

  return (
    <div className="space-y-4">
      {hotels.length === 0 ? (
        <div className="text-center py-8 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
          <Hotel className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">No hotels added yet</p>
          <Button type="button" onClick={addHotel} className="bg-primary-700 text-white rounded-xl">
            <Plus className="h-4 w-4 mr-2" />Add First Hotel
          </Button>
        </div>
      ) : (
        <>
          {hotels.map((hotel, idx) => {
            const isCruise = (hotel.accommodation_type || 'hotel') === 'cruise';
            return (
            <div key={hotel._id || `hotel-idx-${idx}`} className="border-2 border-slate-100 rounded-2xl overflow-hidden bg-white shadow-sm">
              {/* Header */}
              <div 
                className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100 cursor-pointer hover:from-slate-100 hover:to-slate-150 transition-colors"
                onClick={() => setExpandedIndex(expandedIndex === idx ? -1 : idx)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                    <Hotel className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900 block">
                      {hotel.hotelName || `Hotel ${idx + 1}`}
                    </span>
                    {hotel.city && <span className="text-sm text-slate-500">{hotel.city}</span>}
                  </div>
                  {hotel.starRating && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                      {hotel.starRating}
                    </span>
                  )}
                  {hotel.optionNumber ? (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-semibold" data-testid={`hotel-option-badge-${idx}`}>
                      Option {hotel.optionNumber}
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  {hotels.length > 1 && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); removeHotel(idx); }} 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                  {expandedIndex === idx ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                </div>
              </div>

              {/* Expanded Content */}
              {expandedIndex === idx && (
                <div className="p-6 space-y-5 border-t border-slate-100">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">Package option (for comparison itineraries)</Label>
                    <select
                      value={hotel.optionNumber ? String(hotel.optionNumber) : ''}
                      onChange={(e) => updateHotel(idx, 'optionNumber', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                      className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      data-testid={`hotel-option-${idx}`}
                    >
                      <option value="">Not part of an option (shown to all)</option>
                      <option value="1">Option 1</option>
                      <option value="2">Option 2</option>
                    </select>
                    <p className="text-[11px] text-slate-400 mt-1 italic">Set this on hotels that differ between Option 1 and Option 2 so each shows under the right package on the preview.</p>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">Type</Label>
                    <select
                      value={hotel.accommodation_type || 'hotel'}
                      onChange={(e) => updateHotel(idx, 'accommodation_type', e.target.value)}
                      className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      data-testid={`hotel-type-${idx}`}
                    >
                      {ACCOMMODATION_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" /> {isCruise ? 'Ship Photo' : 'Hotel Photo'} (Optional)
                    </Label>
                    <div className="flex items-center gap-4">
                      {hotel.imageUrl ? (
                        <div className="relative w-40 h-28 rounded-xl overflow-hidden border-2 border-slate-200 shadow-sm">
                          <img src={hotel.imageUrl} alt={hotel.hotelName || 'Hotel'} className="w-full h-full object-cover" data-testid={`hotel-photo-preview-${idx}`} />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2 h-7 w-7 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg"
                            onClick={() => updateHotel(idx, 'imageUrl', '')}
                            data-testid={`hotel-photo-remove-${idx}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center w-40 h-28 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageChange(idx, e)}
                            className="hidden"
                            data-testid={`hotel-photo-input-${idx}`}
                          />
                          <div className="text-center">
                            <Upload className="h-7 w-7 text-slate-400 mx-auto mb-1" />
                            <span className="text-xs text-slate-500">Upload Photo</span>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" /> Destination Banner Photo Override (Optional)
                    </Label>
                    <p className="text-[11px] text-slate-400 mb-2">
                      Overrides the auto-curated photo used for the "{hotel.city || '(set a city above)'}" banner
                      in the preview — for this itinerary only.
                    </p>
                    <div className="flex items-center gap-4">
                      {destinationPhotoOverrides && destinationPhotoOverrides[cityKeyFor(hotel.city)] ? (
                        <div className="relative w-40 h-28 rounded-xl overflow-hidden border-2 border-slate-200 shadow-sm">
                          <img
                            src={destinationPhotoOverrides[cityKeyFor(hotel.city)]}
                            alt={hotel.city}
                            className="w-full h-full object-cover"
                            data-testid={`dest-photo-preview-${idx}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2 h-7 w-7 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg"
                            onClick={() => onDestinationPhotoClear && onDestinationPhotoClear(cityKeyFor(hotel.city))}
                            data-testid={`dest-photo-remove-${idx}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <label className={`flex items-center justify-center w-40 h-28 border-2 border-dashed rounded-xl transition-colors ${hotel.city ? 'border-slate-300 cursor-pointer hover:border-primary-500 hover:bg-primary-50' : 'border-slate-200 opacity-50 cursor-not-allowed'}`}>
                          <input
                            type="file"
                            accept="image/*"
                            disabled={!hotel.city}
                            onChange={(e) => handleDestPhotoChange(hotel.city, e)}
                            className="hidden"
                            data-testid={`dest-photo-input-${idx}`}
                          />
                          <div className="text-center">
                            <Upload className="h-7 w-7 text-slate-400 mx-auto mb-1" />
                            <span className="text-xs text-slate-500">Upload Banner Photo</span>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">Hotel Name *</Label>
                      <Input 
                        value={hotel.hotelName || ''} 
                        onChange={(e) => updateHotel(idx, 'hotelName', e.target.value)} 
                        className="h-11 rounded-xl" 
                        placeholder="e.g., Grand Hyatt" 
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">Star Rating</Label>
                      <select 
                        value={hotel.starRating || ''} 
                        onChange={(e) => updateHotel(idx, 'starRating', e.target.value)}
                        className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select rating</option>
                        {STAR_RATINGS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block flex items-center gap-2">
                        <MapPin className="h-4 w-4" /> City/Location
                      </Label>
                      <Input 
                        value={hotel.city || ''} 
                        onChange={(e) => updateHotel(idx, 'city', e.target.value)} 
                        className="h-11 rounded-xl" 
                        placeholder="e.g., Maldives" 
                      />
                    </div>
                    <div className={isCruise ? 'hidden' : ''}>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">Room Type</Label>
                      <Input 
                        value={hotel.roomType || ''} 
                        onChange={(e) => updateHotel(idx, 'roomType', e.target.value)} 
                        className="h-11 rounded-xl" 
                        placeholder="e.g., Deluxe Room, Suite" 
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">Full Address</Label>
                      <Input 
                        value={hotel.address || ''} 
                        onChange={(e) => updateHotel(idx, 'address', e.target.value)} 
                        className="h-11 rounded-xl" 
                        placeholder="Complete hotel address" 
                      />
                    </div>
                  </div>

                  {/* Dates & Room Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100">
                    {isCruise ? (
                      <>
                        <div>
                          <Label className="text-sm text-slate-600 mb-1.5 block flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" /> Sailing date
                          </Label>
                          <Input 
                            type="date"
                            value={hotel.sailing_date || ''} 
                            onChange={(e) => updateHotel(idx, 'sailing_date', e.target.value)} 
                            className="h-10 rounded-lg text-sm"
                            data-testid={`cruise-sailing-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-slate-600 mb-1.5 block flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" /> Docking date
                          </Label>
                          <Input 
                            type="date"
                            value={hotel.docking_date || ''} 
                            onChange={(e) => updateHotel(idx, 'docking_date', e.target.value)} 
                            className="h-10 rounded-lg text-sm"
                            data-testid={`cruise-docking-${idx}`}
                          />
                        </div>
                      </>
                                    ) : (
                      <>
                        <div>
                          <Label className="text-sm text-slate-600 mb-1.5 block flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" /> Check-in
                          </Label>
                          <Input 
                            type="date"
                            value={hotel.checkInDate || ''} 
                            onChange={(e) => updateHotel(idx, 'checkInDate', e.target.value)} 
                            className="h-10 rounded-lg text-sm" 
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-slate-600 mb-1.5 block flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" /> Check-out
                          </Label>
                          <Input 
                            type="date"
                            value={hotel.checkOutDate || ''} 
                            onChange={(e) => updateHotel(idx, 'checkOutDate', e.target.value)} 
                            className="h-10 rounded-lg text-sm" 
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-slate-600 mb-1.5 block">Nights (override)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={hotel.nights ?? ''}
                            onChange={(e) => updateHotel(idx, 'nights', e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0))}
                            className="h-10 rounded-lg text-sm"
                            placeholder="Auto from dates"
                            data-testid={`hotel-nights-${idx}`}
                          />
                        </div>
                      </>
                    )}
                    <div>
                      <Label className="text-sm text-slate-600 mb-1.5 block">{isCruise ? 'No. of Cabins' : 'No. of Rooms'}</Label>
                      <Input 
                        type="number"
                        min="1"
                        value={hotel.rooms ?? hotel.roomCount ?? 1} 
                        onChange={(e) => updateHotel(idx, 'rooms', parseInt(e.target.value) || 1)} 
                        className="h-10 rounded-lg text-sm"
                        data-testid={`hotel-rooms-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 mb-1.5 block flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" /> Pax composition
                      </Label>
                      <Input 
                        value={hotel.pax_composition ?? hotel.occupancy ?? ''} 
                        onChange={(e) => updateHotel(idx, 'pax_composition', e.target.value)} 
                        className="h-10 rounded-lg text-sm" 
                        placeholder="e.g. 2A+1C, 4 sharing, 1 person"
                        data-testid={`hotel-pax-composition-${idx}`}
                      />
                    </div>
                  </div>

                  {/* Cruise-specific fields */}
                  {isCruise && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100" data-testid={`cruise-fields-${idx}`}>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Ship Name</Label>
                        <Input value={hotel.ship_name || ''} onChange={(e) => updateHotel(idx, 'ship_name', e.target.value)} className="h-11 rounded-xl" placeholder="e.g., Cordelia Empress" />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Cruise Line</Label>
                        <Input value={hotel.cruise_line || ''} onChange={(e) => updateHotel(idx, 'cruise_line', e.target.value)} className="h-11 rounded-xl" placeholder="e.g., Cordelia Cruises" />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Cabin Type</Label>
                        <Input value={hotel.cabin_type || ''} onChange={(e) => updateHotel(idx, 'cabin_type', e.target.value)} className="h-11 rounded-xl" placeholder="e.g., Ocean View Cabin" />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Deck</Label>
                        <Input value={hotel.deck || ''} onChange={(e) => updateHotel(idx, 'deck', e.target.value)} className="h-11 rounded-xl" placeholder="e.g., Deck 7" />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Embarkation Port</Label>
                        <Input value={hotel.embarkation_port || ''} onChange={(e) => updateHotel(idx, 'embarkation_port', e.target.value)} className="h-11 rounded-xl" placeholder="e.g., Mumbai" />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Disembarkation Port</Label>
                        <Input value={hotel.disembarkation_port || ''} onChange={(e) => updateHotel(idx, 'disembarkation_port', e.target.value)} className="h-11 rounded-xl" placeholder="e.g., Goa" />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Nights</Label>
                        <Input type="number" min="0" value={hotel.nights ?? ''} onChange={(e) => updateHotel(idx, 'nights', parseInt(e.target.value) || 0)} className="h-11 rounded-xl" placeholder="e.g., 3" />
                      </div>
                    </div>
                  )}

                  {/* Extra Bed */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={hotel.extraBedIncluded || false}
                      onCheckedChange={(checked) => updateHotel(idx, 'extraBedIncluded', checked)}
                    />
                    <span className="text-sm text-slate-700">Extra Bed Included</span>
                  </label>

                  {/* Meal Plan */}
                  <div className="pt-4 border-t border-slate-100">
                    <Label className="text-sm font-semibold text-slate-700 mb-3 block">Meal Plan</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                      {MEAL_PLANS.map(meal => (
                        <label key={meal.id} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                          <Checkbox 
                            checked={(hotel.mealPlan || []).includes(meal.id)}
                            onCheckedChange={() => toggleMealPlan(idx, meal.id)}
                          />
                          <span className="text-xs text-slate-700">{meal.label}</span>
                        </label>
                      ))}
                    </div>
                    {/* Manual / Custom meal plan input — overrides the checkbox grid in PDF if filled */}
                    <div>
                      <Label className="text-xs text-slate-500 mb-1.5 block">Custom Meal Plan (optional)</Label>
                      <Input
                        value={hotel.mealPlanCustom || ''}
                        onChange={(e) => updateHotel(idx, 'mealPlanCustom', e.target.value)}
                        className="h-10 rounded-lg text-sm"
                        placeholder="e.g., Breakfast + Dinner only, Halal meals on request"
                        data-testid={`hotel-meal-custom-${idx}`}
                      />
                      <p className="text-[11px] text-slate-400 mt-1 italic">If filled, this text replaces the checkbox values on the PDF.</p>
                    </div>
                  </div>

                  {/* Hotel Notes — rich text (bullets, bold, etc.) */}
                  <div className="pt-4 border-t border-slate-100">
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">Hotel Notes</Label>
                    <RichTextEditor
                      value={hotel.notes || ''}
                      onChange={(html) => updateHotel(idx, 'notes', html)}
                      placeholder="Special instructions, room preferences, late check-in, view remarks…"
                      minHeight={100}
                      testId={`hotel-notes-${idx}`}
                    />
                  </div>
                </div>
              )}
            </div>
            );
          })}

          <Button 
            type="button" 
            onClick={addHotel} 
            variant="outline"
            className="w-full rounded-xl border-dashed border-2 hover:border-primary-500 hover:bg-primary-50"
          >
            <Plus className="h-4 w-4 mr-2" />Add Another Hotel
          </Button>
        </>
      )}
    </div>
  );
}
